import React from 'react';
import { FileText } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Legal</span>
          </div>
          
          <h1 className="font-display text-4xl lg:text-5xl text-foreground mb-4">
            Terms of Service
          </h1>
          <p className="text-muted-foreground">
            Last updated: August 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-background relative">
        <div className="absolute inset-0 pattern-bg opacity-20" />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="prose prose-slate max-w-none space-y-8">
            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using the First Choice Business Brokers website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Services Description</h2>
              <p className="text-muted-foreground leading-relaxed">
                First Choice Business Brokers provides business brokerage services, including but not limited to business valuations, buyer-seller matching, transaction facilitation, and educational resources through our Newsletter Library. Our services are intended for informational and advisory purposes.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Use of Website</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Use the website in any way that violates applicable laws or regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Attempt to gain unauthorized access to any portion of the website</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Interfere with the proper working of the website</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Copy, reproduce, or distribute any content without authorization</span>
                </li>
              </ul>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                All content on this website, including text, graphics, logos, images, newsletters, and software, is the property of First Choice Business Brokers or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Newsletter Content</h2>
              <p className="text-muted-foreground leading-relaxed">
                The newsletters and educational materials provided on our website are for informational purposes only. They do not constitute professional advice, and you should consult with qualified professionals before making any business decisions. We make no guarantees regarding the accuracy, completeness, or applicability of the information provided.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the fullest extent permitted by law, First Choice Business Brokers shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our website or services.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our website and services are provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not warrant that the website will be uninterrupted, secure, or error-free.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Confidentiality</h2>
              <p className="text-muted-foreground leading-relaxed">
                We understand the sensitive nature of business transactions. Any information you share with us regarding your business will be treated as confidential and will not be disclosed to third parties without your consent, except as required by law or as necessary to provide our services.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the website after any changes constitutes acceptance of the new Terms.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Riverside County, California.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl">
              <h2 className="font-display text-xl text-foreground mb-4">Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 text-muted-foreground">
                <p><strong>First Choice Business Brokers</strong></p>
                <p>3610 Central Ave #400</p>
                <p>Riverside, CA 92506, United States</p>
                <p>Email: montie.barnes@fcbb.com</p>
                <p>Phone: (951) 901-9802</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
